import { Badge, Body1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { BookmarkAdd20Regular } from "@fluentui/react-icons";
import { Fragment, useMemo } from "react";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { usePortfolioStyles } from "@/features/resources/PortfolioViews";
import { openExecutionTask, openScopeInSchedule } from "@/features/tasks/links";
import { appGo } from "@/app/navigate";
import { useProjectStore } from "@/store/project-store";
import { CHANGE_CATEGORIES, CHANGE_CATEGORY_LABEL, CHANGE_KIND_LABEL, diffReportContexts, type ChangeCategory, type ChangeItem } from "./report-model";
import { fmtChangeValue, useMaskedMoney, useReportPoints, useReportStyles } from "./report-ui";

export type CategoryFilter = ChangeCategory | "all";

export function useChangeSet(fromKey: string, toKey: string) {
  const points = useReportPoints();
  const from = points.byKey.get(fromKey) ?? null;
  const to = points.byKey.get(toKey) ?? points.current;
  const a = from ? points.ctx(from.key) : null;
  const b = points.ctx(to.key)!;
  const items = useMemo(() => (a ? diffReportContexts(a, b, points.resources) : []), [a, b, points.resources]);
  return { points, from, to, a, b, items };
}

const useStyles = makeStyles({
  tableSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    flexGrow: 1,
    flexShrink: 0,
  },
  grow: {
    flexGrow: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  table: { tableLayout: "fixed" },
  groupRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  groupCell: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
  },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  cellCompact: {
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  subject: { display: "flex", alignItems: "baseline", gap: tokens.spacingHorizontalS, minWidth: 0 },
  notice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

const KIND_COLOR = { added: "brand", removed: "danger", changed: "informative" } as const;

export function ReportChangesView({
  fromKey,
  toKey,
  category,
  query,
  onPromoted,
  canPromote = false,
}: {
  fromKey: string;
  toKey: string;
  category: CategoryFilter;
  query: string;
  onPromoted: (periodKey: string) => void;
  canPromote?: boolean;
}) {
  const s = useReportStyles();
  const st = useStyles();
  const ps = usePortfolioStyles();
  const masked = useMaskedMoney();
  const activeId = useProjectStore((x) => x.activeId);
  const liveTasks = useProjectStore((x) => x.project.tasks);
  const liveExec = useProjectStore((x) => x.project.executionTasks);
  const { from, to, a, items } = useChangeSet(fromKey, toKey);

  const q = query.trim().toLowerCase();
  const visible = items.filter((i) => (category === "all" || i.category === category) && (!q || `${i.subject} ${i.code ?? ""} ${i.field}`.toLowerCase().includes(q)));
  const groups = CHANGE_CATEGORIES.map((c) => ({ c, rows: visible.filter((i) => i.category === c) })).filter((g) => g.rows.length);

  if (!from || !a) {
    return (
      <div className={s.wrap}>
        <GhostEmptyState title="비교할 시점이 없어요" description="마감이나 체크포인트 뒤에 비교할 수 있습니다." />
      </div>
    );
  }

  const liveIds = new Set(liveTasks.map((t) => t.id));
  const liveExecIds = new Set(liveExec.map((t) => t.id));
  const open = (i: ChangeItem) => {
    if (!activeId || !i.link) return undefined;
    const l = i.link;
    if (l.taskId) return liveIds.has(l.taskId) ? () => openScopeInSchedule(activeId, l.taskId!) : undefined;
    if (l.executionId) return liveExecIds.has(l.executionId) ? () => openExecutionTask(activeId, l.executionId!) : undefined;
    return () => appGo(`/projects/${activeId}/${l.tab}`);
  };

  return (
    <div className={s.wrap}>
      {from.kind === "checkpoint" && (
        <div className={st.notice} role="note">
          <Body1 className={s.secondary}>
            {canPromote
              ? `「${from.label}」은(는) 되돌리기용 체크포인트예요. 이 시점을 보고 기준으로 쓰려면 보고 기간으로 저장하세요.`
              : `「${from.label}」은(는) 되돌리기용 체크포인트예요.`}
          </Body1>
          {canPromote && (
            <FuiButton
              appearance="secondary"
              icon={<BookmarkAdd20Regular />}
              onClick={() => {
                const id = useProjectStore.getState().promoteCheckpointToReport(from.checkpointId!);
                if (id) onPromoted(`rp:${id}`);
              }}
            >
              보고 기간으로 저장
            </FuiButton>
          )}
        </div>
      )}

      <div className={st.tableSection}>
        {visible.length === 0 ? (
          <div className={st.grow}>
            <GhostEmptyState variant={items.length ? "search" : "default"} title={items.length ? undefined : "두 시점 사이에 바뀐 것이 없어요"} description={items.length ? undefined : "편집이 쌓이면 변화가 모입니다."} />
          </div>
        ) : (
          <div className={ps.tableWrap}>
          <table className={mergeClasses(ps.table, st.table)} aria-label={`${from.label}에서 ${to.label}까지 변경 내역`}>
            <colgroup>
              <col style={{ width: 64 }} />
              <col />
              <col style={{ width: 104 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 132 }} />
            </colgroup>
            <thead>
              <tr>
                <th className={ps.th}>구분</th>
                <th className={ps.th}>항목</th>
                <th className={ps.th}>변경</th>
                <th className={mergeClasses(ps.th, ps.num)}>이전</th>
                <th className={mergeClasses(ps.th, ps.num)}>이후</th>
                <th className={mergeClasses(ps.th, ps.num)}>차이</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.c}>
                  <tr className={st.groupRow}>
                    <td colSpan={6} className={st.groupCell}>
                      {CHANGE_CATEGORY_LABEL[g.c]} <span className={s.tertiary}>{g.rows.length}</span>
                    </td>
                  </tr>
                  {g.rows.map((i) => {
                    const onClick = open(i);
                    const cell = mergeClasses(ps.td, st.cellCompact);
                    const toneClass = i.tone === "danger" ? s.danger : i.tone === "warning" ? s.warning : i.tone === "success" ? s.success : undefined;
                    return (
                      <tr
                        key={i.id}
                        className={onClick ? st.row : undefined}
                        tabIndex={onClick ? 0 : undefined}
                        onClick={onClick}
                        onKeyDown={onClick ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick()) : undefined}
                      >
                        <td className={cell}>
                          <Badge appearance="tint" size="small" shape="rounded" color={KIND_COLOR[i.kind]}>
                            {CHANGE_KIND_LABEL[i.kind]}
                          </Badge>
                        </td>
                        <td className={cell}>
                          <div className={st.subject}>
                            {i.code && <span className={s.code}>{i.code}</span>}
                            <span className={s.truncate} title={i.subject}>
                              {i.subject}
                            </span>
                          </div>
                        </td>
                        <td className={mergeClasses(cell, s.secondary)}>{i.field}</td>
                        <td className={mergeClasses(cell, ps.num, s.tertiary, s.truncate)} title={fmtChangeValue(i.before, masked)}>
                          {fmtChangeValue(i.before, masked)}
                        </td>
                        <td className={mergeClasses(cell, ps.num, s.truncate)} title={fmtChangeValue(i.after, masked)}>
                          {fmtChangeValue(i.after, masked)}
                        </td>
                        <td className={mergeClasses(cell, ps.num, toneClass)}>{i.delta !== undefined ? fmtChangeValue(i.delta, masked, true) : ""}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
